<template>
  <div class="enr-overlay" @click.self="close">
    <div class="enr-modal">

      <!-- ── Header ── -->
      <div class="enr-header">
        <div class="enr-header-main">
          <div class="enr-avatar" v-if="hasData && !loadingData">
            {{ initials }}
          </div>
          <div class="enr-avatar enr-avatar-sk" v-else></div>
          <div class="enr-header-info">
            <div class="enr-header-name" v-if="hasData && !loadingData">
              {{ enrollmentData.fname }} {{ enrollmentData.lname }}
              <span class="enr-id-badge">ENR #{{ enrollmentData.id }}</span>
              <span class="enr-status-badge" :class="transmitStatusClass" v-if="enrollmentData.transmit_status">
                {{ transmitStatusLabel }}
              </span>
            </div>
            <div class="enr-header-name enr-header-name-sk" v-else></div>
            <div class="enr-header-sub" v-if="hasData && !loadingData">
              <span v-if="enrollmentData.service_name">
                <i class="fa fa-fingerprint"></i> {{ enrollmentData.service_name }}
              </span>
              <span v-if="enrollmentData.tcn" class="enr-sub-tcn">
                <i class="fa fa-barcode"></i> {{ enrollmentData.tcn }}
                <button class="btn-tcn-copy" :title="copiedTcn ? 'Copied!' : 'Copy TCN'" @click.stop="copyTcn(enrollmentData.tcn)">
                  <i :class="copiedTcn ? 'fa fa-check' : 'fa fa-copy'"></i>
                </button>
              </span>
            </div>
            <div class="enr-header-sub-sk" v-else></div>
          </div>
        </div>
        <button class="enr-close" @click="close" title="Close"><i class="fa fa-times"></i></button>
      </div>

      <!-- ── Skeleton ── -->
      <div class="enr-skeleton" v-if="loadingData">
        <div class="sk-cols">
          <div class="sk-left">
            <div class="sk-card" v-for="n in 3" :key="n">
              <div class="sk-card-title"></div>
              <div class="sk-row" v-for="r in (n === 3 ? 2 : 5)" :key="r">
                <div class="sk-label"></div>
                <div class="sk-value" :style="{ width: (50 + (r * 13) % 40) + '%' }"></div>
              </div>
            </div>
          </div>
          <div class="sk-right">
            <div class="sk-card sk-logs">
              <div class="sk-card-title"></div>
              <div class="sk-log-row" v-for="r in 7" :key="r">
                <div class="sk-log-dot"></div>
                <div class="sk-log-date"></div>
                <div class="sk-log-desc" :style="{ width: (40 + (r * 11) % 35) + '%' }"></div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- ── No data ── -->
      <div class="enr-empty" v-else-if="!hasData">
        <i class="fa fa-inbox"></i>
        <p>Enrollment details not found.</p>
      </div>

      <!-- ── Body ── -->
      <div class="enr-body" v-else>
        <div class="enr-cols">

          <!-- Left: detail cards -->
          <div class="enr-detail-col">

            <!-- Personal -->
            <div class="detail-card">
              <div class="detail-card-title"><i class="fa fa-user"></i> Personal Details</div>
              <div class="detail-grid">
                <div class="detail-row">
                  <span class="dl">First Name</span>
                  <span class="dv">{{ enrollmentData.fname || '—' }}</span>
                </div>
                <div class="detail-row">
                  <span class="dl">Last Name</span>
                  <span class="dv">{{ enrollmentData.lname || '—' }}</span>
                </div>
                <div class="detail-row">
                  <span class="dl">Email</span>
                  <span class="dv">{{ enrollmentData.email || '—' }}</span>
                </div>
                <div class="detail-row">
                  <span class="dl">Contact</span>
                  <span class="dv">{{ enrollmentData.phone_no || '—' }}</span>
                </div>
                <div class="detail-row">
                  <span class="dl">Address</span>
                  <span class="dv">{{ enrollmentData.address1 || '—' }}<span v-if="enrollmentData.city">, {{ enrollmentData.city }}</span></span>
                </div>
              </div>
            </div>

            <!-- Enrollment -->
            <div class="detail-card">
              <div class="detail-card-title"><i class="fa fa-fingerprint"></i> Enrollment Details</div>
              <div class="detail-grid">
                <div class="detail-row">
                  <span class="dl">Enroll Date</span>
                  <span class="dv">{{ enrollmentData.enroll_date ? dateFormat(enrollmentData.enroll_date) : '—' }}</span>
                </div>
                <div class="detail-row">
                  <span class="dl">ORI</span>
                  <span class="dv mono">{{ enrollmentData.ori || '—' }}</span>
                </div>
                <div class="detail-row">
                  <span class="dl">CRI</span>
                  <span class="dv mono">{{ enrollmentData.cri || '—' }}</span>
                </div>
                <div class="detail-row">
                  <span class="dl">TCN</span>
                  <span class="dv">
                    <span class="mono">{{ enrollmentData.tcn || '—' }}</span>
                    <span class="tcn-note" v-if="enrollmentData.tcn">
                      <template v-if="enrollmentData.tcn_list && enrollmentData.tcn_list !== ''">
                        · sent {{ datetimeFormatAmPm(enrollmentData.tcn_list.created_at) }}
                      </template>
                    </span>
                  </span>
                </div>
                <div class="detail-row" v-if="enrollmentData.tcr && enrollmentData.tcr !== ''">
                  <span class="dl">TCR</span>
                  <span class="dv">
                    <span class="mono">{{ enrollmentData.tcr }}</span>
                    <span class="tcn-note" v-if="enrollmentData.tcr_list && enrollmentData.tcr_list !== ''">
                      · sent {{ datetimeFormatAmPm(enrollmentData.tcr_list.created_at) }}
                    </span>
                  </span>
                </div>
              </div>
            </div>

            <!-- Appointment -->
            <div class="detail-card">
              <div class="detail-card-title"><i class="fa fa-calendar-alt"></i> Appointment</div>
              <div class="detail-grid">
                <div class="detail-row">
                  <span class="dl">App #</span>
                  <span class="dv">{{ enrollmentData.appointment_id || '—' }}</span>
                </div>
                <div class="detail-row">
                  <span class="dl">Scheduled</span>
                  <span class="dv">{{ enrollmentData.schedule_date || '—' }}</span>
                </div>
              </div>
            </div>

          </div>

          <!-- Right: logs -->
          <div class="enr-logs-col">
            <div class="detail-card logs-card">
              <div class="detail-card-title">
                <i class="fa fa-list-alt"></i> Activity Logs
                <button class="btn-assign" v-if="!current_display_logs" @click="backtologs">
                  <i class="fa fa-arrow-left"></i> Back
                </button>
              </div>
              <enrollmentlogs v-if="current_display_logs" :logsdata="getenrollmentLog"></enrollmentlogs>
              <enrollassigncompany
                v-if="!current_display_logs"
                :companydata="filteredCompanies"
                :enrollmentData="enrollmentData"
                v-on:postdata="funPostDate"
                v-on:backtologs="backtologs"
              ></enrollassigncompany>
            </div>
          </div>

        </div>

        <!-- deporder / authrcp responses -->
        <div class="enr-section-row"
          v-if="enrollmentData.service_type === 'deporder' || enrollmentData.service_type === 'authrcp'">
          <div class="response-card" v-if="enrollmentData.msg_filedata !== ''">
            <div class="detail-card-title"><i class="fa fa-envelope"></i> Message</div>
            <div class="response-body">
              <div v-for="(line, i) in enrollmentData.msg_filedata.split('\n').slice(0, -1)" :key="i" v-show="line !== ''">{{ line }}</div>
            </div>
          </div>
          <div class="response-card" v-if="enrollmentData.res_filedata !== ''">
            <div class="detail-card-title"><i class="fa fa-check-circle"></i> Acknowledgement</div>
            <div class="response-body">
              <div v-for="(line, i) in enrollmentData.res_filedata.split('\n').slice(0, -1)" :key="i" v-show="line !== ''">{{ line }}</div>
            </div>
          </div>
        </div>

        <!-- FDLE responses -->
        <div class="enr-section-row" v-if="enrollmentData.service_type === 'fdle'">
          <div class="response-card" v-if="enrollmentData.fdle_response !== ''">
            <div class="detail-card-title"><i class="fa fa-shield-alt"></i> FDLE Response</div>
            <div class="response-body">{{ enrollmentData.fdle_response }}</div>
          </div>
          <div class="response-card" v-if="enrollmentData.acha_response !== ''">
            <div class="detail-card-title"><i class="fa fa-hospital"></i> AHCA Response</div>
            <div class="response-body">{{ enrollmentData.acha_response }}</div>
          </div>
          <div class="response-card full-w" v-if="enrollmentData.fdle_submission_errors">
            <div class="detail-card-title"><i class="fa fa-exclamation-triangle"></i> FDLE Submission Errors</div>
            <div class="response-body">
              <div v-for="(e, i) in enrollmentData.fdle_submission_errors" :key="i">
                <div v-html="e.error_message"></div>
                <hr class="err-hr" v-if="i + 1 !== enrollmentData.fdle_submission_errors.length" />
              </div>
            </div>
          </div>
        </div>

        <!-- FDLE submission status -->
        <div class="fdle-status-box" v-if="show_fdle_report">
          <div class="detail-card-title"><i class="fa fa-satellite-dish"></i> FDLE Submission Status</div>
          <div class="enr-empty" v-if="reportDataLoading">
            <loadingSpinner></loadingSpinner>
          </div>
          <div class="fdle-kv" v-else>
            <template v-if="getValuesReport && getValuesReport.length">
              <div class="fdle-kv-row" v-for="data in getValuesReport" :key="data.key">
                <span class="fdle-key">{{ data.key }}</span>
                <span class="fdle-val" :class="{ 'fdle-error': data.key === 'Message' }">{{ data.value }}</span>
              </div>
            </template>
            <div class="enr-empty" v-if="!getValuesReport || getValuesReport.length === 0">
              <i class="fa fa-inbox"></i>
              <p>No data found</p>
            </div>
          </div>
        </div>

      </div>

      <!-- ── Footer ── -->
      <div class="enr-footer">
        <button class="btn-fdle"
          v-if="hasData && !loadingData && enrollmentData.service_type === 'fdle'"
          @click="!reportDataLoading ? checkFdleStatus(enrollmentData.id) : null"
          :disabled="reportDataLoading">
          <i class="fa fa-satellite-dish"></i> Check FDLE Status
        </button>
        <button class="btn-close-modal" @click="close">
          <i class="fa fa-times"></i> Close
        </button>
      </div>

    </div>
  </div>
</template>

<script>
import loadingSpinner from "@/components/frontend/spinner.vue";
import enrollmentlogs from "@/components/frontend/modal/enrollmentlogs";
import enrollassigncompany from "@/components/frontend/modal/enrollassigncompany";
import moment from "moment";
import { mapGetters } from "vuex";
import { ASSIGN_ENROLLMENT_COMPANY, FETCH_FDLE_SUBMISSION_STATUS } from "@/core/services/store/actions.type";

export default {
  props: {
    showModal:      { type: Boolean, default: false },
    loadingData:    { type: Boolean, default: false },
    hasData:        { type: Boolean, default: false },
    showQrCode:     { type: Boolean, default: false },
    indexNum:       { type: Number, default: -1 },
    enrollmentData: { type: Object, default: () => ({}) },
    token:          { type: String, default: "" },
    paymentLog:     { type: Boolean, default: false },
    displayLogs:    { type: Boolean },
  },
  components: { loadingSpinner, enrollmentlogs, enrollassigncompany },
  data() {
    return {
      current_display_logs: this.displayLogs ?? false,
      show_fdle_report: false,
      reports: [],
      reportDataLoading: false,
      copiedTcn: false,
    };
  },
  watch: {
    displayLogs(newVal) { this.current_display_logs = newVal; },
  },
  computed: {
    ...mapGetters(["currentUser", "getenrollmentLog", "getCompanies", "getMasterCompanies"]),
    filteredCompanies() {
      return (this.getMasterCompanies || []).filter(item => item.use_in_archive);
    },
    getValuesReport() {
      return Object.entries(this.reports).map(([key, value]) => ({ key, value }));
    },
    initials() {
      const f = this.enrollmentData.fname || "";
      const l = this.enrollmentData.lname || "";
      return ((f[0] || "") + (l[0] || "")).toUpperCase() || "?";
    },
    transmitStatusLabel() {
      const s = this.enrollmentData.transmit_status;
      if (s === "100" || s === "400") return "Draft";
      if (s === "300" || s === "600") return "Transmitted";
      if (s === "200" || s === "500") {
        return this.enrollmentData.service_type === "fingerprintcard" ? "Final" : "Ready to Transmit";
      }
      return null;
    },
    transmitStatusClass() {
      const s = this.enrollmentData.transmit_status;
      if (s === "100" || s === "400") return "ts-draft";
      if (s === "300" || s === "600") return "ts-sent";
      if (s === "200" || s === "500") return "ts-ready";
      return "";
    },
  },
  methods: {
    dateFormat(date) {
      if (!date) return "—";
      return moment(date).format("MM/DD/YYYY");
    },
    datetimeFormatAmPm(date) {
      if (!date) return "";
      return moment(date).format("MM/DD/YYYY h:mm a");
    },
    copyTcn(tcn) {
      navigator.clipboard.writeText(tcn).then(() => {
        this.copiedTcn = true;
        setTimeout(() => { this.copiedTcn = false; }, 1800);
      });
    },
    close() { this.$emit("closemodal"); },
    backtologs() { this.current_display_logs = true; },
    show_company() { this.current_display_logs = false; },
    funPostDate(companyId, company_name) {
      if (!companyId) { this.$toastr.e("Please select a company"); return; }
      this.$swal.fire({
        title: "Are you sure?",
        text: "You want to assign to company " + company_name,
        icon: "warning",
        showCancelButton: true,
        confirmButtonColor: "#3085d6",
        cancelButtonColor: "#d33",
        confirmButtonText: "Yes",
        cancelButtonText: "No",
      }).then(result => {
        if (result.isConfirmed) {
          this.$store.dispatch(ASSIGN_ENROLLMENT_COMPANY, { id: this.enrollmentData.id, selected_company: companyId })
            .then(() => {
              this.$toastr.s("Enrollment updated successfully");
              this.$emit("refreshtable", this.indexNum, company_name);
              this.close();
            })
            .catch(err => { this.$toastr.e(err.data.msg); });
        }
      });
    },
    checkFdleStatus(enrollment_id) {
      this.reportDataLoading = true;
      this.show_fdle_report = true;
      this.$store.dispatch(FETCH_FDLE_SUBMISSION_STATUS, enrollment_id)
        .then(res => { this.reports = res.data; this.reportDataLoading = false; })
        .catch(() => {
          this.$toastr.e("Error fetching FDLE submission status");
          this.reportDataLoading = false;
        });
    },
  },
  filters: {
    toFixedTwoDigit(amount) { return parseFloat(amount).toFixed(2); },
  },
};
</script>

<style lang="scss" scoped>
$font: 'Segoe UI', sans-serif;
$blue: #389ad4;
$dark: #0d1b3e;
$border: #e8ecf4;
$green: #276749;

/* ── Overlay ── */
.enr-overlay {
  position: fixed;
  inset: 0;
  z-index: 9100;
  background: rgba(5, 13, 36, 0.6);
  backdrop-filter: blur(4px);
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 16px;
  font-family: $font;
}

/* ── Modal shell ── */
.enr-modal {
  background: #fff;
  border-radius: 16px;
  box-shadow: 0 32px 80px rgba(5, 13, 36, 0.28);
  width: 100%;
  max-width: 1120px;
  max-height: 94vh;
  display: flex;
  flex-direction: column;
  overflow: hidden;
}

/* ── Header ── */
.enr-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 14px 20px;
  background: linear-gradient(135deg, #0d1b3e 0%, #1a2f5e 100%);
  flex-shrink: 0;
  gap: 12px;
}

.enr-header-main {
  display: flex;
  align-items: center;
  gap: 14px;
  min-width: 0;
}

.enr-avatar {
  width: 44px;
  height: 44px;
  border-radius: 12px;
  background: rgba(255,255,255,0.15);
  border: 1.5px solid rgba(255,255,255,0.25);
  color: #fff;
  font-size: 16px;
  font-weight: 700;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  letter-spacing: 0.5px;
}

.enr-avatar-sk {
  animation: shimmer 1.4s infinite linear;
  background: linear-gradient(90deg, rgba(255,255,255,0.08) 25%, rgba(255,255,255,0.16) 50%, rgba(255,255,255,0.08) 75%);
  background-size: 300px 100%;
}

.enr-header-info {
  min-width: 0;
}

.enr-header-name {
  font-size: 15px;
  font-weight: 700;
  color: #fff;
  display: flex;
  align-items: center;
  gap: 8px;
  flex-wrap: wrap;
}

.enr-header-name-sk {
  height: 14px;
  width: 200px;
  border-radius: 4px;
  animation: shimmer 1.4s infinite linear;
  background: linear-gradient(90deg, rgba(255,255,255,0.08) 25%, rgba(255,255,255,0.16) 50%, rgba(255,255,255,0.08) 75%);
  background-size: 300px 100%;
  margin-bottom: 8px;
}

.enr-id-badge {
  font-size: 11px;
  font-weight: 600;
  background: rgba(255,255,255,0.14);
  border: 1px solid rgba(255,255,255,0.2);
  color: rgba(255,255,255,0.9);
  border-radius: 6px;
  padding: 2px 8px;
}

.enr-status-badge {
  font-size: 10px;
  font-weight: 700;
  letter-spacing: 0.4px;
  border-radius: 20px;
  padding: 2px 8px;
  text-transform: uppercase;

  &.ts-draft  { background: #fef3c7; color: #92400e; }
  &.ts-ready  { background: #dbeafe; color: #1e40af; }
  &.ts-sent   { background: #dcfce7; color: #166534; }
}

.enr-header-sub {
  display: flex;
  align-items: center;
  gap: 14px;
  margin-top: 5px;
  font-size: 12px;
  color: rgba(255,255,255,0.55);
  flex-wrap: wrap;

  span { display: flex; align-items: center; gap: 5px; }
  i { opacity: 0.7; font-size: 11px; }
}

.enr-sub-tcn {
  font-family: 'Courier New', monospace;
  color: rgba(255,255,255,0.65) !important;
}

.enr-header-sub-sk {
  height: 10px;
  width: 280px;
  border-radius: 3px;
  margin-top: 8px;
  animation: shimmer 1.4s infinite linear;
  background: linear-gradient(90deg, rgba(255,255,255,0.06) 25%, rgba(255,255,255,0.12) 50%, rgba(255,255,255,0.06) 75%);
  background-size: 300px 100%;
}

.btn-tcn-copy {
  flex-shrink: 0;
  width: 18px;
  height: 18px;
  border: 1px solid rgba(255,255,255,0.25);
  border-radius: 4px;
  background: rgba(255,255,255,0.08);
  color: rgba(255,255,255,0.6);
  font-size: 9px;
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 0;
  transition: all 0.15s;

  &:hover { background: rgba(255,255,255,0.18); color: #fff; }
  .fa-check { color: #6ee7b7; }
}

.enr-close {
  flex-shrink: 0;
  width: 32px;
  height: 32px;
  border-radius: 8px;
  border: 1px solid rgba(255,255,255,0.18);
  background: transparent;
  color: rgba(255,255,255,0.6);
  font-size: 13px;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.15s;

  &:hover { background: rgba(255,255,255,0.14); color: #fff; }
}

/* ── Empty ── */
.enr-empty {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 48px 24px;
  gap: 10px;
  color: #a0aab8;
  font-size: 13px;
  flex: 1;

  i { font-size: 28px; opacity: 0.4; }
  p { margin: 0; }
}

/* ── Body ── */
.enr-body {
  flex: 1;
  overflow-y: auto;
  padding: 16px 20px;
  display: flex;
  flex-direction: column;
  gap: 14px;
  background: #f5f7fb;
}

.enr-cols {
  display: flex;
  gap: 14px;
  align-items: flex-start;
}

.enr-detail-col {
  display: flex;
  flex-direction: column;
  gap: 10px;
  width: 300px;
  flex-shrink: 0;
}

.enr-logs-col {
  flex: 1;
  min-width: 0;
  display: flex;
  flex-direction: column;
}

/* ── Detail cards ── */
.detail-card {
  background: #fff;
  border: 1px solid $border;
  border-radius: 10px;
  padding: 12px 14px;
}

.logs-card {
  flex: 1;
  min-height: 280px;
}

.detail-card-title {
  font-size: 10px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.6px;
  color: #a0aab8;
  margin-bottom: 10px;
  display: flex;
  align-items: center;
  gap: 6px;

  i { color: $blue; font-size: 11px; }
}

.detail-grid {
  display: flex;
  flex-direction: column;
  gap: 6px;
}

.detail-row {
  display: flex;
  align-items: baseline;
  gap: 8px;
  font-size: 12.5px;
}

.dl {
  font-size: 11px;
  color: #a0aab8;
  white-space: nowrap;
  min-width: 80px;
  flex-shrink: 0;
}

.dv {
  color: $dark;
  font-weight: 500;
  word-break: break-word;
}

.mono {
  font-family: 'Courier New', monospace;
  font-size: 12px;
}

.tcn-note {
  font-size: 10px;
  color: #9ca3af;
  font-weight: 400;
  margin-left: 4px;
}

/* ── Response / FDLE sections ── */
.enr-section-row {
  display: flex;
  gap: 14px;
  flex-wrap: wrap;
}

.response-card {
  flex: 1;
  min-width: 220px;
  background: #fff;
  border: 1px solid $border;
  border-radius: 10px;
  padding: 12px 14px;

  &.full-w { flex-basis: 100%; }
}

.response-body {
  font-size: 12px;
  color: #4a5568;
  line-height: 1.65;
  word-break: break-word;
  background: #f9fafc;
  border: 1px solid #eef0f6;
  border-radius: 6px;
  padding: 10px 12px;
  max-height: 150px;
  overflow-y: auto;
}

.err-hr {
  border: none;
  border-top: 1px solid #f0f3fa;
  margin: 10px 0;
}

/* ── FDLE status ── */
.fdle-status-box {
  background: #fff;
  border: 1px solid $border;
  border-radius: 10px;
  padding: 12px 14px;
}

.fdle-kv {
  display: flex;
  flex-direction: column;
  gap: 6px;
}

.fdle-kv-row {
  display: flex;
  align-items: baseline;
  gap: 10px;
  font-size: 12.5px;
}

.fdle-key {
  font-weight: 600;
  color: $dark;
  min-width: 120px;
  flex-shrink: 0;
  font-size: 12px;
}

.fdle-val  { color: #4a5568; }
.fdle-error { color: #c62828; font-weight: 600; }

/* ── Footer ── */
.enr-footer {
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: 10px;
  padding: 12px 20px;
  border-top: 1px solid $border;
  background: #fff;
  flex-shrink: 0;
}

.btn-fdle {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  height: 34px;
  padding: 0 14px;
  border-radius: 7px;
  border: 1.5px solid $blue;
  background: transparent;
  color: $blue;
  font-size: 13px;
  font-weight: 600;
  font-family: $font;
  cursor: pointer;
  transition: background 0.15s;
  margin-right: auto;

  &:hover:not(:disabled) { background: #eef7fb; }
  &:disabled { opacity: 0.5; cursor: default; }
}

.btn-close-modal {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  height: 34px;
  padding: 0 16px;
  border-radius: 7px;
  border: none;
  background: #e8ecf4;
  color: #4a5568;
  font-size: 13px;
  font-weight: 600;
  font-family: $font;
  cursor: pointer;
  transition: background 0.15s;

  &:hover { background: #d8dde8; }
}

.btn-assign {
  margin-left: auto;
  display: inline-flex;
  align-items: center;
  gap: 5px;
  height: 22px;
  padding: 0 8px;
  border-radius: 5px;
  border: 1px solid $border;
  background: #fff;
  color: #7a8499;
  font-size: 11px;
  font-family: $font;
  cursor: pointer;
  transition: all 0.15s;

  &:hover { border-color: $blue; color: $blue; }
}

/* ── Skeleton ── */
@keyframes shimmer {
  0%   { background-position: -600px 0; }
  100% { background-position:  600px 0; }
}

%sk {
  border-radius: 4px;
  background: linear-gradient(90deg, #eef0f6 25%, #f5f7fb 50%, #eef0f6 75%);
  background-size: 600px 100%;
  animation: shimmer 1.4s infinite linear;
}

.enr-skeleton {
  flex: 1;
  padding: 16px 20px;
  background: #f5f7fb;
  overflow: hidden;
}

.sk-cols {
  display: flex;
  gap: 14px;
}

.sk-left {
  width: 300px;
  flex-shrink: 0;
  display: flex;
  flex-direction: column;
  gap: 10px;
}

.sk-right { flex: 1; }

.sk-card {
  background: #fff;
  border: 1px solid #eef0f6;
  border-radius: 10px;
  padding: 12px 14px;
  display: flex;
  flex-direction: column;
  gap: 9px;
}

.sk-logs { min-height: 280px; }

.sk-card-title {
  @extend %sk;
  height: 9px;
  width: 100px;
  margin-bottom: 2px;
}

.sk-row {
  display: flex;
  align-items: center;
  gap: 10px;
}

.sk-label {
  @extend %sk;
  height: 9px;
  width: 72px;
  flex-shrink: 0;
}

.sk-value {
  @extend %sk;
  height: 9px;
}

.sk-log-row {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 7px 0;
  border-bottom: 1px solid #f5f7fa;
}

.sk-log-dot {
  @extend %sk;
  width: 8px;
  height: 8px;
  border-radius: 50%;
  flex-shrink: 0;
}

.sk-log-date {
  @extend %sk;
  width: 90px;
  height: 9px;
  flex-shrink: 0;
}

.sk-log-desc {
  @extend %sk;
  height: 9px;
  flex: 1;
}
</style>
